import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import config from 'cityads-v3/config'

import TablePagination from '@mui/material/TablePagination'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'

import classes from './Paginator.scss'

export default function Paginator({
  page,
  onPageChange,
  onRowsPerPageChange = null,
  display = config.PAGE_PAGINATION_DISPLAY_COUNT,
  rowsPerPageOptions = config.PAGE_LIMIT_KEYS,
  ...rest
}) {
  const { t } = useTranslation(['main'])
  const labelDisplayedRows = useCallback(({ from, to, count }) => t('displayRows', { from, to, count }), [t])

  return (
    <TablePagination
      ActionsComponent={TablePaginationActions}
      component={({ t, tReady, i18n, i18nLoadedAt, ...rest }) => <div {...rest} />}
      classes={{
        ...rest.classes,
        root: classes.root,
        select: classes.select,
        spacer: classes.spacer,
        selectRoot: classes.selectRoot,
        toolbar: classes.toolbar
      }}
      labelRowsPerPage={<span className={classes.limitby}>{t('limitBy')}</span>}
      labelDisplayedRows={labelDisplayedRows}
      display={display}
      rowsPerPageOptions={rowsPerPageOptions}
      {...rest}

      page={page - 1}
      onPageChange={(evt, page) => {
        onPageChange && onPageChange(evt, page + 1)
      }}

      onRowsPerPageChange={evt => {
        onRowsPerPageChange?.(evt.target.value)
      }}
    />
  )
}

Paginator.propTypes = {
  page: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
}

function TablePaginationActions({ count, onPageChange, page, rowsPerPage }) {
  const handleFirstPageButtonClick = useCallback(event => {
    onPageChange(event, 0)
  }, [onPageChange])

  const handleBackButtonClick = useCallback(event => {
    onPageChange(event, page - 1)
  }, [onPageChange, page])

  const handleNextButtonClick = useCallback(event => {
    onPageChange(event, page + 1)
  }, [onPageChange, page])

  const handleLastPageButtonClick = useCallback(event => {
    onPageChange(
      event,
      Math.max(0, Math.ceil(count / rowsPerPage) - 1),
    )
  }, [count, onPageChange, rowsPerPage])

  return (
    <div className={classes.navigation}>
      <IconButton
        size="large"
        classes={{
          root: classes.smHide
        }}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
      >
        <Icon>first_page</Icon>
      </IconButton>
      <IconButton
        size="large"
        onClick={handleBackButtonClick}
        disabled={page === 0}
      >
        <Icon>chevron_left</Icon>
      </IconButton>
      <IconButton
        size="large"
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        <Icon>chevron_right</Icon>
      </IconButton>
      <IconButton
        size="large"
        classes={{
          root: classes.smHide
        }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        <Icon>last_page</Icon>
      </IconButton>
    </div>
  )
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
}
